import { motion, type HTMLMotionProps } from 'framer-motion';
import { Link } from 'react-router-dom';

interface ButtonProps extends HTMLMotionProps<'button'> {
  variant?: 'primary' | 'secondary' | 'accent' | 'outlined' | 'glass' | 'ghost';
  to?: string;
  className?: string;
  icon?: string;
  children: React.ReactNode;
}

/**
 * Button - A versatile, animated CTA component for the entire app.
 */
const Button = ({
  variant = 'primary',
  to,
  className = '',
  icon,
  children,
  ...props
}: ButtonProps) => {
  const baseStyles =
    'group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 font-bold text-[15px] rounded-xl transition-all duration-300 transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent';

  const variants = {
    primary:
      'bg-primary text-white hover:bg-primary/90 shadow-xl shadow-primary/25 hover:shadow-primary/40 hover:-translate-y-0.5',
    secondary: 'bg-secondary text-white hover:bg-dark shadow-lg shadow-black/20 hover:-translate-y-0.5',
    accent:
      'bg-primary text-white hover:bg-primary/90 shadow-xl shadow-primary/25 hover:-translate-y-0.5',
    outlined:
      'border border-gray-200 text-secondary hover:bg-primary/5 hover:border-primary/30 hover:text-primary',
    glass: 'bg-white/10 backdrop-blur-md border border-white/15 text-white hover:bg-white/20',
    ghost: 'bg-transparent text-secondary hover:bg-secondary/5 font-semibold px-4',
  };

  const content = (
    <>
      {children}
      {icon && (
        <i className={`${icon} group-hover:translate-x-1 transition-transform duration-200`}></i>
      )}
    </>
  );

  const combinedClasses = `${baseStyles} ${variants[variant]} ${className}`;

  if (to) {
    return (
      <Link to={to} className={combinedClasses}>
        {content}
      </Link>
    );
  }

  return (
    <motion.button
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      className={combinedClasses}
      {...props}
    >
      {content}
    </motion.button>
  );
};

export default Button;
